import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Table } from 'antd';
import dayjs from 'dayjs';
import useUser from 'utils/useUser';

import TaskCell from './cells/TaskCell';
import PatientCell from './cells/PatientCell';
import PodPriorityCell from './cells/PodPriorityCell';
import PriorityBadge from './cells/PriorityBadge';
import ReporterCell from './cells/ReporterCell';
import CompletedCell from './cells/CompletedCell';
import ActionsCell from './cells/ActionsCell';
import s from './TasksTable.module.scss';

import type { FC } from 'react';
import type { ColumnsType } from 'antd/es/table';
import type { SorterResult } from 'antd/es/table/interface';
import type { TFunction } from 'i18next';
import type {
  IDashboardTaskRow,
  SortField,
  SortOrder,
  TabKey,
} from 'pages/TasksDashboard/types';

const renderCreatedDate = (date: string) => {
  if (!date) return '-';
  const parsed = dayjs(date);
  if (!parsed.isValid()) return '-';
  return parsed.format('MM/DD/YYYY');
};

const getOpenPoolColumns = (
  currentUserGuid: string,
  t: TFunction,
  activeTab: TabKey,
  assigningGuids: Set<string>,
  onAssign: (taskGuid: string) => void,
  completingGuids: Set<string>,
  onComplete: (taskGuid: string) => void,
  onReturn: (taskGuid: string) => void,
  returningGuid: string | null,
  onEdit: (taskGuid: string) => void,
  sortBy: SortField | undefined,
  sortOrder: SortOrder | undefined,
): ColumnsType<IDashboardTaskRow> => [
  {
    title: t('tasksDashboard.columns.task'),
    dataIndex: 'title',
    key: 'title',
    width: '28%',
    render: (_: string, record: IDashboardTaskRow) => (
      <TaskCell
        title={record.title}
        quickNotes={record.quickNotes}
        returnedAt={record.returnedAt}
        status={record.status}
        reportedByGuid={record.reportedByGuid}
        currentUserGuid={currentUserGuid}
        activeTab={activeTab}
        onEdit={() => onEdit(record.guid)}
        isBusy={
          completingGuids.has(record.guid) || returningGuid === record.guid
        }
      />
    ),
  },
  {
    title: t('tasksDashboard.columns.patient'),
    dataIndex: 'patientName',
    key: 'patientName',
    width: '16%',
    ellipsis: true,
    render: (_: string, record: IDashboardTaskRow) => (
      <PatientCell
        patientGuid={record.patientGuid}
        patientName={record.patientName}
      />
    ),
  },
  {
    title: t('tasksDashboard.columns.podPriority'),
    key: 'podPriority',
    width: '12%',
    render: (_: unknown, record: IDashboardTaskRow) => (
      <PodPriorityCell podContext={record.podContext} />
    ),
  },
  {
    title: t('tasksDashboard.columns.priority'),
    dataIndex: 'priority',
    key: 'priority',
    width: '10%',
    sorter: true,
    sortOrder: sortBy === 'priority' ? sortOrder : null,
    render: (priority: string) => <PriorityBadge priority={priority} />,
  },
  {
    title: t('tasksDashboard.columns.reporter'),
    dataIndex: 'reporterName',
    key: 'reporterName',
    width: '17%',
    ellipsis: true,
    render: (_: string, record: IDashboardTaskRow) => (
      <ReporterCell
        reporterTitle={record.reporterTitle}
        reporterName={record.reporterName}
        reportedByGuid={record.reportedByGuid}
        currentUserGuid={currentUserGuid}
      />
    ),
  },
  {
    title: t('tasksDashboard.columns.createdDate'),
    dataIndex: 'createdAt',
    key: 'createdAt',
    width: '14%',
    sorter: true,
    sortOrder: sortBy === 'createdAt' ? sortOrder : null,
    render: renderCreatedDate,
  },
  {
    key: 'actions',
    width: activeTab === 'assigned_to_me' ? 90 : 50,
    align: activeTab === 'assigned_to_me' ? 'left' : 'center',
    render: (_: unknown, record: IDashboardTaskRow) => (
      <ActionsCell
        task={record}
        activeTab={activeTab}
        assigningGuids={assigningGuids}
        onAssign={onAssign}
        completingGuids={completingGuids}
        onComplete={onComplete}
        onReturn={onReturn}
        returningGuid={returningGuid}
      />
    ),
  },
];

const getCompletedColumns = (
  currentUserGuid: string,
  t: TFunction,
  activeTab: TabKey,
  sortBy: SortField | undefined,
  sortOrder: SortOrder | undefined,
): ColumnsType<IDashboardTaskRow> => [
  {
    title: t('tasksDashboard.columns.task'),
    dataIndex: 'title',
    key: 'title',
    width: '25%',
    render: (_: string, record: IDashboardTaskRow) => (
      <TaskCell
        title={record.title}
        quickNotes={record.quickNotes}
        returnedAt={record.returnedAt}
        status={record.status}
        reportedByGuid={record.reportedByGuid}
        currentUserGuid={currentUserGuid}
        activeTab={activeTab}
      />
    ),
  },
  {
    title: t('tasksDashboard.columns.patient'),
    dataIndex: 'patientName',
    key: 'patientName',
    width: '15%',
    ellipsis: true,
    render: (_: string, record: IDashboardTaskRow) => (
      <PatientCell
        patientGuid={record.patientGuid}
        patientName={record.patientName}
      />
    ),
  },
  {
    title: t('tasksDashboard.columns.podPriority'),
    key: 'podPriority',
    width: '12%',
    render: (_: unknown, record: IDashboardTaskRow) => (
      <PodPriorityCell podContext={record.podContext} />
    ),
  },
  {
    title: t('tasksDashboard.columns.priority'),
    dataIndex: 'priority',
    key: 'priority',
    width: '10%',
    sorter: true,
    sortOrder: sortBy === 'priority' ? sortOrder : null,
    render: (priority: string) => <PriorityBadge priority={priority} />,
  },
  {
    title: t('tasksDashboard.columns.reporter'),
    dataIndex: 'reporterName',
    key: 'reporterName',
    width: '12%',
    ellipsis: true,
    render: (_: string, record: IDashboardTaskRow) => (
      <ReporterCell
        reporterTitle={record.reporterTitle}
        reporterName={record.reporterName}
        reportedByGuid={record.reportedByGuid}
        currentUserGuid={currentUserGuid}
      />
    ),
  },
  {
    title: t('tasksDashboard.columns.createdDate'),
    dataIndex: 'createdAt',
    key: 'createdAt',
    width: '11%',
    sorter: true,
    sortOrder: sortBy === 'createdAt' ? sortOrder : null,
    render: renderCreatedDate,
  },
  {
    title: t('tasksDashboard.columns.completed'),
    key: 'completed',
    width: '15%',
    render: (_: unknown, record: IDashboardTaskRow) => (
      <CompletedCell
        completedByTitle={record.completedByTitle}
        completedByName={record.completedByName}
        completedByGuid={record.completedByGuid}
        completedAt={record.completedAt}
        currentUserGuid={currentUserGuid}
      />
    ),
  },
];

interface ITasksTableProps {
  tasks: IDashboardTaskRow[];
  total: number;
  isLoading: boolean;
  activeTab: TabKey;
  offset: number;
  limit: number;
  onPageChange: (page: number) => void;
  assigningGuids: Set<string>;
  onAssign: (taskGuid: string) => void;
  completingGuids: Set<string>;
  onComplete: (taskGuid: string) => void;
  onReturn: (taskGuid: string) => void;
  returningGuid: string | null;
  onEdit: (taskGuid: string) => void;
  sortBy?: SortField;
  sortOrder?: SortOrder;
  onSortChange: (
    field: SortField | undefined,
    order: SortOrder | undefined,
  ) => void;
}

const TasksTable: FC<ITasksTableProps> = ({
  tasks,
  total,
  isLoading,
  activeTab,
  offset,
  limit,
  onPageChange,
  assigningGuids,
  onAssign,
  completingGuids,
  onComplete,
  onReturn,
  returningGuid,
  onEdit,
  sortBy,
  sortOrder,
  onSortChange,
}) => {
  const { t } = useTranslation();
  const { loginGuid: currentUserGuid } = useUser();

  const columns = useMemo(
    () =>
      activeTab === 'completed'
        ? getCompletedColumns(currentUserGuid, t, activeTab, sortBy, sortOrder)
        : getOpenPoolColumns(
            currentUserGuid,
            t,
            activeTab,
            assigningGuids,
            onAssign,
            completingGuids,
            onComplete,
            onReturn,
            returningGuid,
            onEdit,
            sortBy,
            sortOrder,
          ),
    [
      activeTab,
      currentUserGuid,
      t,
      assigningGuids,
      onAssign,
      completingGuids,
      onComplete,
      onReturn,
      returningGuid,
      onEdit,
      sortBy,
      sortOrder,
    ],
  );

  const handleTableChange = useCallback(
    (
      _pagination: unknown,
      _filters: unknown,
      sorter:
        | SorterResult<IDashboardTaskRow>
        | SorterResult<IDashboardTaskRow>[],
    ) => {
      const single = Array.isArray(sorter) ? sorter[0] : sorter;
      const field = (single?.columnKey as SortField) || undefined;
      const order = (single?.order as SortOrder) || undefined;
      if (field === sortBy && order === sortOrder) return;
      onSortChange(field, order);
    },
    [onSortChange, sortBy, sortOrder],
  );

  return (
    <Table<IDashboardTaskRow>
      className={s.table}
      columns={columns}
      dataSource={tasks}
      rowKey="guid"
      loading={isLoading}
      size="small"
      bordered
      tableLayout="fixed"
      onChange={handleTableChange}
      pagination={{
        current: limit > 0 ? Math.floor(offset / limit) + 1 : 1,
        pageSize: limit,
        total,
        showSizeChanger: false,
        onChange: (page) => onPageChange((page - 1) * limit),
      }}
    />
  );
};

export default TasksTable;
